import type { CartItem, CatalogueBundledMemberV2, ProductCode, ResolvedCatalogue, ResolvedVariant } from 'models/purchaseFlowRevamp'

/** A pack is never a cart line itself: choosing it adds its members as ordinary lines. */
export const isPack = (variant: ResolvedVariant | undefined): boolean => variant?.bundleType === 'PACK'

/** The pack's members the client left ticked. */
export const selectedPackMembers = (
  pack: ResolvedVariant,
  unselectedCodes: readonly ProductCode[]
): CatalogueBundledMemberV2[] => (pack.bundledProducts ?? []).filter((member) => !unselectedCodes.includes(member.productCode))

/**
 * The cart lines a pack adds: one ordinary line per ticked member, in the member's own product group, at the
 * member's price, each tagged with the pack and this pack's selection id. The backend resolves the
 * members for the session (area band, price book), so a member the catalogue does not serve is skipped.
 */
/** What a pack member carries besides itself, already narrowed to what its variant offers and priced. */
export interface PackMemberLineAddons {
  optionValues: CartItem['optionValues']
  extraServiceIds: CartItem['extraServiceIds']
  extraQuantities: CartItem['extraQuantities']
  /** The add-ons' total for one unit, or null when there are none. */
  addonsPrice: string | null
}

export function packCartLines(
  pack: ResolvedVariant,
  catalogue: ResolvedCatalogue | undefined,
  selectionId: string,
  resolvedAt: string,
  unselectedCodes: readonly ProductCode[] = [],
  addonsFor: (member: CatalogueBundledMemberV2) => PackMemberLineAddons | null = () => null
): Omit<CartItem, 'id'>[] {
  const lines: Omit<CartItem, 'id'>[] = []
  const packName = catalogue?.bundles?.find((bundle) => bundle.variants.some((variant) => variant.id === pack.productCode))?.displayName
  for (const member of selectedPackMembers(pack, unselectedCodes)) {
    const shell = catalogue?.shellProducts.find((s) => (s.variants ?? []).some((v) => v.id === member.productCode))
    const variant = shell?.variants.find((v) => v.id === member.productCode)
    if (!shell || !variant) continue

    const unitPrice = member.price ? member.price.amount.toFixed(2) : variant.price?.unitPrice
    const currency = member.price?.currency ?? variant.price?.currency
    if (!unitPrice || !currency) continue

    const addons = addonsFor(member)
    lines.push({
      shellProductId: shell.id,
      variantId: variant.id,
      quantity: 1,
      optionValues: addons?.optionValues ?? {},
      extraServiceIds: addons?.extraServiceIds ?? [],
      extraQuantities: addons?.extraQuantities ?? {},
      priceSnapshot: { unitPrice, currency, resolvedAt, ...(addons?.addonsPrice ? { addonsPrice: addons.addonsPrice } : {}) },
      pack: { productCode: pack.productCode, selectionId, ...(packName ? { name: packName } : {}) },
      productName: shell.displayName,
    })
  }
  return lines
}
